import React, { useCallback, useEffect, useRef, useState } from 'react';

type Pan = { x: number; y: number };
type PanStart = { x: number; y: number; screenX: number; screenY: number } | null;

type UseCanvasPanZoomArgs = {
	containerRef: React.RefObject<HTMLDivElement | null>;
	minScale: number;
	maxScale: number;
	tapMaxMovePx?: number;
	wheelZoomFactor?: number;
	/**
	 * When false, the canvas root is not in the DOM (e.g. `CanvasView` returns null while loading).
	 * The wheel listener must attach after this becomes true, or zoom-by-scroll never binds.
	 */
	containerReady?: boolean;
};

type UseCanvasPanZoomResult = {
	pan: Pan;
	scale: number;
	panStart: PanStart;
	setPan: React.Dispatch<React.SetStateAction<Pan>>;
	setViewport: (viewport: { pan: Pan; scale: number }) => void;
	setScaleAroundViewportOffset: (nextScale: number, offsetX: number, offsetY: number) => void;
	handleScaleControlChange: (nextScale: number) => void;
	handleCanvasPointerDown: (e: React.PointerEvent) => void;
	handleCanvasPointerMove: (e: React.PointerEvent) => void;
	handleCanvasPointerUp: (e: React.PointerEvent) => void;
	consumeTapCancelled: () => boolean;
	getWorldFromClientPoint: (clientX: number, clientY: number) => { x: number; y: number } | null;
};

const DEFAULT_TAP_MAX_MOVE_PX = 6;
const DEFAULT_WHEEL_ZOOM_FACTOR = 0.002;

export default function useCanvasPanZoom({
	containerRef,
	minScale,
	maxScale,
	tapMaxMovePx = DEFAULT_TAP_MAX_MOVE_PX,
	wheelZoomFactor = DEFAULT_WHEEL_ZOOM_FACTOR,
	containerReady = true,
}: UseCanvasPanZoomArgs): UseCanvasPanZoomResult {
	const [pan, setPan] = useState<Pan>({ x: 0, y: 0 });
	const [scale, setScale] = useState(1);
	const [panStart, setPanStart] = useState<PanStart>(null);
	const panRef = useRef(pan);
	const scaleRef = useRef(scale);
	panRef.current = pan;
	scaleRef.current = scale;

	const canvasTapCancelledRef = useRef(false);
	const canvasPointerOriginRef = useRef<{ x: number; y: number } | null>(null);
	const activeTouchPointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
	const pinchStateRef = useRef<{ initialDistance: number; initialScale: number } | null>(null);
	const accumulatedDeltaYRef = useRef(0);
	const lastCursorOffsetRef = useRef({ x: 0, y: 0 });
	const zoomRafIdRef = useRef<number | null>(null);
	/**
	 * Drag-panning arrives as raw `pointermove` events, which (unlike `pointermove` outside React, or the
	 * wheel path below) React dispatches synchronously at native event rate rather than coalescing to one
	 * per frame. Applying `setPan` directly on every event was driving more re-renders than the display
	 * could show, each one re-rendering every canvas card. Instead, the target pan accumulates in a ref
	 * and gets committed once per animation frame.
	 */
	const pendingPanRef = useRef<Pan | null>(null);
	const panRafIdRef = useRef<number | null>(null);

	const setScaleAroundViewportOffset = useCallback((nextScale: number, offsetX: number, offsetY: number) => {
		const oldScale = scaleRef.current;
		const clampedScale = Math.min(maxScale, Math.max(minScale, nextScale));
		if (clampedScale === oldScale) return;

		const { x: panX, y: panY } = panRef.current;
		const k = clampedScale / oldScale;
		const newPanX = offsetX * (1 - k) + panX * k;
		const newPanY = offsetY * (1 - k) + panY * k;

		setScale(clampedScale);
		setPan({ x: newPanX, y: newPanY });
	}, [maxScale, minScale]);

	const handleCanvasPointerDown = useCallback((e: React.PointerEvent) => {
		const card = (e.target as HTMLElement).closest('[data-canvas-card]');
		if (card && !card.hasAttribute('data-position-locked')) return;
		// A locked card keeps its own pointer capture so it still sees the release (tap-to-select) and
		// swallows the click; its pointer moves bubble up here and pan the canvas.
		if (!card) containerRef.current?.setPointerCapture?.(e.pointerId);
		if (e.pointerType === 'touch') {
			activeTouchPointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
			const touchPointers = Array.from(activeTouchPointersRef.current.values());
			if (touchPointers.length >= 2) {
				const [p1, p2] = touchPointers;
				pinchStateRef.current = {
					initialDistance: Math.hypot(p1.x - p2.x, p1.y - p2.y),
					initialScale: scaleRef.current,
				};
				canvasTapCancelledRef.current = true;
				canvasPointerOriginRef.current = null;
				setPanStart(null);
				return;
			}
		}
		canvasTapCancelledRef.current = false;
		canvasPointerOriginRef.current = { x: e.clientX, y: e.clientY };
		setPanStart({ x: panRef.current.x, y: panRef.current.y, screenX: e.clientX, screenY: e.clientY });
	}, [containerRef]);

	const handleCanvasPointerMove = useCallback((e: React.PointerEvent) => {
		if (e.pointerType === 'touch') {
			activeTouchPointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
			const touchPointers = Array.from(activeTouchPointersRef.current.values());
			// Any multi-touch on the canvas is treated as pinch: never pan the background while 2+ fingers are down.
			if (touchPointers.length >= 2) {
				let pinchState = pinchStateRef.current;
				if (!pinchState) {
					const [p1, p2] = touchPointers;
					pinchState = {
						initialDistance: Math.hypot(p1.x - p2.x, p1.y - p2.y),
						initialScale: scaleRef.current,
					};
					pinchStateRef.current = pinchState;
					canvasTapCancelledRef.current = true;
					canvasPointerOriginRef.current = null;
					setPanStart(null);
				}
				const [p1, p2] = touchPointers;
				const nextDistance = Math.hypot(p1.x - p2.x, p1.y - p2.y);
				if (pinchState.initialDistance > 0 && nextDistance > 0) {
					const nextScale = pinchState.initialScale * (nextDistance / pinchState.initialDistance);
					const el = containerRef.current;
					if (!el) return;
					const rect = el.getBoundingClientRect();
					const centerX = rect.width / 2;
					const centerY = rect.height / 2;
					const midpointX = (p1.x + p2.x) / 2 - rect.left;
					const midpointY = (p1.y + p2.y) / 2 - rect.top;
					setScaleAroundViewportOffset(nextScale, midpointX - centerX, midpointY - centerY);
				}
				canvasTapCancelledRef.current = true;
				return;
			}
		}
		if (!panStart) return;
		const origin = canvasPointerOriginRef.current;
		if (origin) {
			const d = Math.hypot(e.clientX - origin.x, e.clientY - origin.y);
			if (d > tapMaxMovePx) {
				canvasTapCancelledRef.current = true;
			}
		}
		const nextPan = { x: panStart.x + (e.clientX - panStart.screenX), y: panStart.y + (e.clientY - panStart.screenY) };
		panRef.current = nextPan;
		pendingPanRef.current = nextPan;
		if (panRafIdRef.current === null) {
			panRafIdRef.current = requestAnimationFrame(() => {
				panRafIdRef.current = null;
				if (pendingPanRef.current) {
					setPan(pendingPanRef.current);
					pendingPanRef.current = null;
				}
			});
		}
	}, [containerRef, panStart, setScaleAroundViewportOffset, tapMaxMovePx]);

	const handleCanvasPointerUp = useCallback((e: React.PointerEvent) => {
		containerRef.current?.releasePointerCapture?.(e.pointerId);
		activeTouchPointersRef.current.delete(e.pointerId);
		if (activeTouchPointersRef.current.size < 2) {
			pinchStateRef.current = null;
		}
		if (panRafIdRef.current !== null) {
			cancelAnimationFrame(panRafIdRef.current);
			panRafIdRef.current = null;
		}
		if (pendingPanRef.current) {
			setPan(pendingPanRef.current);
			pendingPanRef.current = null;
		}
		setPanStart(null);
		canvasPointerOriginRef.current = null;
	}, [containerRef]);

	useEffect(() => {
		return () => {
			if (panRafIdRef.current !== null) cancelAnimationFrame(panRafIdRef.current);
		};
	}, []);

	const consumeTapCancelled = useCallback(() => {
		if (!canvasTapCancelledRef.current) return false;
		canvasTapCancelledRef.current = false;
		return true;
	}, []);

	const getWorldFromClientPoint = useCallback((clientX: number, clientY: number) => {
		const el = containerRef.current;
		if (!el) return null;
		const rect = el.getBoundingClientRect();
		const w = rect.width;
		const h = rect.height;
		const cx = clientX - rect.left;
		const cy = clientY - rect.top;
		const { x: panX, y: panY } = panRef.current;
		const currentScale = scaleRef.current;
		return {
			x: (cx - (w / 2 + panX)) / currentScale,
			y: (cy - (h / 2 + panY)) / currentScale,
		};
	}, [containerRef]);

	useEffect(() => {
		if (!containerReady) return;
		const el = containerRef.current;
		if (!el) return;

		const applyZoom = () => {
			zoomRafIdRef.current = null;
			const deltaY = accumulatedDeltaYRef.current;
			accumulatedDeltaYRef.current = 0;
			if (deltaY === 0) return;

			const { x: cursorOffsetX, y: cursorOffsetY } = lastCursorOffsetRef.current;
			setScaleAroundViewportOffset(scaleRef.current - deltaY * wheelZoomFactor, cursorOffsetX, cursorOffsetY);
		};

		const onWheel = (e: WheelEvent) => {
			e.preventDefault();
			const rect = el.getBoundingClientRect();
			const centerX = rect.width / 2;
			const centerY = rect.height / 2;
			lastCursorOffsetRef.current = {
				x: e.clientX - rect.left - centerX,
				y: e.clientY - rect.top - centerY,
			};
			accumulatedDeltaYRef.current += e.deltaY;

			if (zoomRafIdRef.current === null) {
				zoomRafIdRef.current = requestAnimationFrame(applyZoom);
			}
		};

		el.addEventListener('wheel', onWheel, { passive: false });
		return () => {
			el.removeEventListener('wheel', onWheel);
			if (zoomRafIdRef.current !== null) {
				cancelAnimationFrame(zoomRafIdRef.current);
				zoomRafIdRef.current = null;
			}
		};
	}, [containerReady, containerRef, setScaleAroundViewportOffset, wheelZoomFactor]);

	const setViewport = useCallback((viewport: { pan: Pan; scale: number }) => {
		setPan(viewport.pan);
		setScale(viewport.scale);
	}, []);

	const handleScaleControlChange = useCallback((nextScale: number) => {
		// Keep zoom anchored to viewport center for slider interactions.
		setScaleAroundViewportOffset(nextScale, 0, 0);
	}, [setScaleAroundViewportOffset]);

	return {
		pan,
		scale,
		panStart,
		setPan,
		setViewport,
		setScaleAroundViewportOffset,
		handleScaleControlChange,
		handleCanvasPointerDown,
		handleCanvasPointerMove,
		handleCanvasPointerUp,
		consumeTapCancelled,
		getWorldFromClientPoint,
	};
}
